import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type { AdminV2GrowthSegmentFilterDefinition } from "@/types/adminV2";
import type {
  CrmActionScope,
  CrmActionScopeAction,
  CrmActionScopeBroadcastPreviewSample,
  CrmActionScopeEntityType,
  CrmActionScopeResponse,
  CrmActionScopeSourceSurface,
} from "@/vnext/routes/people/shared/crmActionScopes";
import type { CrmLinkedTagView } from "@/vnext/routes/people/shared/crmLinkedTagView";
import type { CrmActionScopeMatchingSelection } from "@/vnext/routes/people/shared/crmMatchingSelection";

export interface CrmActionScopeOwnerPreviewResponse {
  owner: {
    displayName: string | null;
    email: string | null;
    id: string;
  } | null;
  scope: CrmActionScope;
  targetCount: number;
}

export interface CrmActionScopeOwnerAssignmentResponse {
  owner: {
    displayName: string | null;
    email: string | null;
    id: string;
  } | null;
  scopeId: string;
  updatedCount: number;
}

export interface CrmActionScopeCombinePreviewResponse {
  outcome: "confirmation_records_review_no_merge";
  pair: Array<{
    label: string;
    position: 1 | 2;
  }>;
  scope: CrmActionScope;
  targetCount: number;
}

export interface CrmActionScopeCombineReviewResponse {
  outcome: "review_recorded_no_merge";
  review: {
    id: string;
    reviewRequired: false;
    status: "recorded";
  };
  scopeId: string;
}

export interface CrmActionScopeTagPreviewResponse {
  operation: "apply" | "remove";
  scope: CrmActionScope;
  tag: {
    color: string | null;
    id: string;
    name: string;
    slug: string;
  };
  targetCount: number;
  existingAssignmentCount: number;
  willChangeCount: number;
}

export interface CrmActionScopeTagOption {
  color: string | null;
  id: string;
  name: string;
  slug: string;
  archivedAt: string | null;
  isSystemManaged: boolean;
  readOnlyReason: "system" | "merged" | "legacy_scope" | null;
  selectedAssignmentCount: number;
}

export interface CrmActionScopeTagsResponse {
  assignmentTarget?: "selected_records";
  tags: CrmActionScopeTagOption[];
  currentTags: CrmActionScopeTagOption[];
  memberCount: number;
  canCreate: boolean;
}

export interface CrmActionScopeTagMutationResponse {
  operation: "apply" | "remove";
  scopeId: string;
  tag: {
    color: string | null;
    id: string;
    name: string;
    slug: string;
  };
  updatedCount: number;
}

export interface CrmActionScopeBroadcastPreviewResponse {
  audience?: { mode: "primary_user" | "all_users"; count: number };
  category: "marketing" | "transactional";
  counts: Record<string, number>;
  evaluatedAt: string;
  recipientSetDigest: string;
  routingOrganisationId: string;
  sample: CrmActionScopeBroadcastPreviewSample[];
  scope: CrmActionScope;
  sendableCount: number;
}

export const buildAdminV2CrmActionScopeEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  createAdminV2CrmActionScope: builder.mutation<
    CrmActionScopeResponse,
    {
      action: CrmActionScopeAction;
      entityIds: string[];
      entityType: CrmActionScopeEntityType;
      filterDefinition?: AdminV2GrowthSegmentFilterDefinition;
      sourceScopeDigest?: string;
      /** Native selected-tab context for audit; explicit IDs remain exact. */
      scopeFilters?: string;
      scopeFilterMatchLogic?: "all" | "any";
      sourceSurface: CrmActionScopeSourceSurface;
      /** "Select all N matching": `entityIds` stays empty (add_tag only). */
      selection?: CrmActionScopeMatchingSelection;
      /** Linked-pair tag filters: body top level, never inside `selection`. */
      tagView?: CrmLinkedTagView;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/crm/action-scopes",
      method: "POST",
      data: body,
      // A matching (All N) refusal is explained inside the Tags drawer; the
      // app-wide access dialog would cover it and block Cancel.
      ...(body.selection ? { suppressAccessDeniedModal: true } : {}),
    }),
  }),

  getAdminV2CrmActionScope: builder.query<
    CrmActionScopeResponse,
    { scopeId: string }
  >({
    query: ({ scopeId }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}`,
    }),
    providesTags: (_result, _error, { scopeId }) => [
      { type: "AdminV2CrmActionScopes", id: scopeId },
    ],
    keepUnusedDataFor: 20,
  }),

  getAdminV2CrmActionScopeTags: builder.query<
    CrmActionScopeTagsResponse,
    { q?: string; scopeId: string; assignmentTarget?: "selected_records" }
  >({
    query: ({ scopeId, q, assignmentTarget }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/tags`,
      ...(Boolean(q?.trim()) || Boolean(assignmentTarget)
        ? {
            params: {
              ...(q?.trim() ? { q: q.trim() } : {}),
              ...(assignmentTarget ? { assignmentTarget } : {}),
            },
          }
        : {}),
    }),
    providesTags: (_result, _error, { scopeId }) => [
      { type: "AdminV2CrmActionScopes", id: `tags:${scopeId}` },
    ],
    // Apply consumes this selection. Do not refetch its library on success;
    // a fresh panel creates a new scope and obtains a fresh library instead.
    keepUnusedDataFor: 0,
  }),

  previewAdminV2CrmActionScopeCombinePreview: builder.mutation<
    CrmActionScopeCombinePreviewResponse,
    { memberDigest: string; scopeId: string }
  >({
    query: ({ scopeId, memberDigest }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/combine-preview`,
      method: "POST",
      data: { memberDigest },
    }),
  }),

  previewAdminV2CrmActionScopeCombine: builder.mutation<
    CrmActionScopeCombineReviewResponse,
    { memberDigest: string; scopeId: string; confirmed: true }
  >({
    query: ({ scopeId, memberDigest, confirmed }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/combine-review`,
      method: "POST",
      data: { memberDigest, confirmed },
    }),
    invalidatesTags: (_result, _error, { scopeId }) => [
      { type: "AdminV2CrmActionScopes", id: scopeId },
    ],
  }),

  previewAdminV2CrmActionScopeOwner: builder.mutation<
    CrmActionScopeOwnerPreviewResponse,
    {
      memberDigest: string;
      ownerStaffUserId: string | null;
      scopeId: string;
    }
  >({
    query: ({ scopeId, memberDigest, ownerStaffUserId }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/owner-preview`,
      method: "POST",
      data: { memberDigest, ownerStaffUserId },
    }),
  }),

  assignAdminV2CrmActionScopeOwner: builder.mutation<
    CrmActionScopeOwnerAssignmentResponse,
    {
      confirmed: true;
      memberDigest: string;
      ownerStaffUserId: string | null;
      scopeId: string;
    }
  >({
    query: ({ scopeId, memberDigest, ownerStaffUserId, confirmed }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/owner-assignment`,
      method: "POST",
      data: { memberDigest, ownerStaffUserId, confirmed },
    }),
    invalidatesTags: [
      { type: "AdminV2CrmPeople", id: "LIST" },
      { type: "AdminV2CrmUsers", id: "LIST" },
      { type: "AdminV2CrmOrganisations", id: "LIST" },
      { type: "AdminV2CrmFirms", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  previewAdminV2GrowthTagActionScope: builder.mutation<
    CrmActionScopeTagPreviewResponse,
    {
      memberDigest: string;
      operation: "apply" | "remove";
      scopeId: string;
      tagId: string;
    }
  >({
    query: ({ scopeId, tagId, memberDigest, operation }) => ({
      url: `/admin/v2/growth/tags/action-scopes/${encodeURIComponent(scopeId)}/preview`,
      method: "POST",
      data: { tagId, memberDigest, operation },
    }),
  }),

  applyAdminV2GrowthTagActionScope: builder.mutation<
    CrmActionScopeTagMutationResponse,
    {
      confirmed: true;
      memberDigest: string;
      operation: "apply" | "remove";
      scopeId: string;
      tagId: string;
    }
  >({
    query: ({ scopeId, tagId, memberDigest, operation, confirmed }) => ({
      url: `/admin/v2/growth/tags/action-scopes/${encodeURIComponent(scopeId)}/apply`,
      method: "POST",
      data: { tagId, memberDigest, operation, confirmed },
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthTags", id: "LIST" },
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  applyAdminV2CrmTagChanges: builder.mutation<
    {
      scopeId: string;
      updatedCount: number;
      createdTagIds: string[];
      assignmentTarget?: "selected_records";
      /** Records changed on their own tags only (ambiguous linked pair, TAG-03). */
      ownOnlyCount?: number;
    },
    {
      scopeId: string;
      memberDigest: string;
      addTagIds: string[];
      removeTagIds: string[];
      newTagNames: string[];
      assignmentTarget?: "selected_records";
    }
  >({
    query: ({ scopeId, ...data }) => ({
      url: `/admin/v2/crm/action-scopes/${encodeURIComponent(scopeId)}/tag-changes`,
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "AdminV2CrmActionScopes", id: result.scopeId },
            "AdminV2GrowthTags",
            "AdminV2CrmPeople",
            "AdminV2CrmUsers",
            "AdminV2CrmOrganisations",
            "AdminV2CrmFirms",
            "AdminV2AuditLog",
          ]
        : [],
  }),

  previewAdminV2GrowthCampaignActionScope: builder.mutation<
    CrmActionScopeBroadcastPreviewResponse,
    {
      category: "marketing" | "transactional";
      memberDigest: string;
      audienceMode?: "primary_user" | "all_users";
      sampleLimit?: number;
      scopeId: string;
    }
  >({
    query: ({ scopeId, memberDigest, ...body }) => ({
      url: `/admin/v2/growth/campaigns/action-scopes/${encodeURIComponent(scopeId)}/preview`,
      method: "POST",
      data: { memberDigest, ...body },
    }),
  }),
});
